import type { IntlShape } from 'react-intl/src/types';

import type { CountryCodeEnumType } from '@/types/regionTypes';

import { PREFER_NOT_TO_SAY } from '@/lib/forms/const';
import { formMessages } from '@/messages/forms';

/**
 * Get the user title label. The label is placed before the user's name
 * @returns Empty string if no option available. Otherwise, the label with an ending empty space `" "`.
 * @example
 * getUserTitleLabel('US', { label: 'Sra.', value: 'Ms' });
 * // returns 'Sra. '
 */
export const getUserTitleLabel = (
  countryCode?: CountryCodeEnumType,
  option?: {
    label?: string | null;
    value?: string | null;
  } | null,
): string => {
  if (countryCode === 'JP') return '';
  if (option?.value && ['Mr', 'Mrs', 'Ms'].includes(option?.value)) {
    return option?.label ? option.label + ' ' : '';
  }

  return '';
};

export const getUserTitleLabelByValue = (
  formatMessage: IntlShape['formatMessage'],
  countryCode?: CountryCodeEnumType,
  value?: string | null,
): string => {
  const option = getTitleOptionByValue(formatMessage, countryCode, value);
  return getUserTitleLabel(countryCode, option);
};

/** Japan has no Mrs option; stored Mrs maps to Ms. */
export const normalizeStoredTitle = (
  countryCode: CountryCodeEnumType | undefined,
  title?: string | null,
) => (countryCode === 'JP' && title === 'Mrs' ? 'Ms' : title);

export const getTitleOptionByValue = (
  formatMessage: IntlShape['formatMessage'],
  countryCode?: CountryCodeEnumType,
  value?: string | null,
) => {
  const options = getTitleOptions(formatMessage, countryCode);
  return options.find((t) => t.value === value) ?? null;
};

export const getTitleOptions = (
  formatMessage: IntlShape['formatMessage'],
  countryCode?: CountryCodeEnumType,
) => {
  const isJapan = countryCode === 'JP';

  return [
    {
      label: formatMessage(isJapan ? formMessages.male : formMessages.mr),
      value: 'Mr',
    },
    ...(isJapan
      ? []
      : [{ label: formatMessage(formMessages.mrs), value: 'Mrs' }]),
    {
      label: formatMessage(isJapan ? formMessages.female : formMessages.ms),
      value: 'Ms',
    },
    {
      label: formatMessage(formMessages.preferNotToSay),
      value: PREFER_NOT_TO_SAY,
    },
  ];
};
